// Route helpers for the shell: match the current path to app/routes.tsx, find the permission a
// path needs (derived from app/nav.ts), and list navigable routes for ⌘K.
import { matchPath } from 'react-router'
import { routes, type Layout, type RouteDef } from '@/app/routes'
import { storeNav, platformNav, settingsSections } from '@/app/nav'
import type { Permission } from '@shared/types'

/** The route definition for a pathname (first match in table order, like <Routes>). */
export function matchRoute(pathname: string): RouteDef | null {
  // <Routes> ranks static segments above params; emulate by trying exact static paths first.
  const exact = routes.find(r => !r.path.includes(':') && matchPath({ path: r.path, end: true }, pathname))
  if (exact) return exact
  return routes.find(r => matchPath({ path: r.path, end: true }, pathname)) ?? null
}

const permByPath: Array<[string, Permission]> = (() => {
  const out: Array<[string, Permission]> = []
  for (const sec of [...storeNav, ...platformNav]) for (const it of sec.items) {
    if (it.perm) {
      out.push([it.to, it.perm])
      for (const c of it.children ?? []) out.push([c.to, it.perm])
    }
  }
  for (const s of settingsSections) if ('perm' in s && s.perm) out.push([s.to, s.perm as Permission])
  // Longest prefix wins.
  return out.sort((a, b) => b[0].length - a[0].length)
})()

/** Permission needed to open a path (null = any member). */
export function permissionFor(pathname: string): Permission | null {
  for (const [p, perm] of permByPath) {
    if (p === '/') continue
    if (pathname === p || pathname.startsWith(p + '/')) return perm
  }
  return null
}

/** Routes offered by ⌘K "Navigate" for a shell layout (no param routes). */
export function navigableRoutes(kind: 'store' | 'platform'): RouteDef[] {
  const layouts: Layout[] = kind === 'store' ? ['store', 'full'] : ['platform']
  return routes.filter(r => layouts.includes(r.layout) && !r.path.includes(':'))
}

/** Parent list path for a detail route ("/orders/:id" → "/orders"), used when switching stores. */
export function listPathFor(pathname: string): string {
  const r = matchRoute(pathname)
  if (!r) return '/'
  if (!r.path.includes(':') && !/\/new$/.test(r.path)) return pathname
  const base = r.path.split('/:')[0].replace(/\/new$/, '')
  return base || '/'
}

/** Section label used in ⌘K ("Settings", "Online store", "Products"…). */
export function sectionFor(path: string): string {
  if (path.startsWith('/settings')) return 'Settings'
  if (path.startsWith('/platform')) return 'Platform'
  for (const sec of storeNav) for (const it of sec.items) {
    if (it.to !== '/' && (path === it.to || path.startsWith(it.to + '/') || it.children?.some(c => c.to === path))) return it.label
  }
  return ''
}
